import type { AthleteProfile } from "../api/types";
import {
  distanceLabel,
  EXPERIENCE_LABEL,
  formatDate,
  formatDuration,
  formatPace,
  metersToKm,
  paceOf,
  paceShort,
  PREFERRED_TIME_LABEL,
  rangeText,
  WEEKDAYS,
} from "../lib/format";
import { t } from "../i18n";

const orDash = (v: string | null | undefined) => (v && v.trim() ? v : "—");

export function ProfileSummary({ profile, showCoachFields = true }: { profile: AthleteProfile | null; showCoachFields?: boolean }) {
  if (!profile) return <p className="muted">{t("Perfil esportivo ainda não preenchido.")}</p>;
  return (
    <div className="stack">
      <dl className="facts">
        <dt>{t("Experiência")}</dt>
        <dd>{profile.running_experience ? EXPERIENCE_LABEL[profile.running_experience] : "—"}</dd>
        <dt>{t("Corre desde")}</dt>
        <dd>{formatDate(profile.running_since)}</dd>
        <dt>{t("Volume semanal")}</dt>
        <dd className="num">{metersToKm(profile.usual_weekly_volume_m)}</dd>
        <dt>{t("Dias preferidos")}</dt>
        <dd>{profile.preferred_days.length ? profile.preferred_days.map((d) => WEEKDAYS[d]).join(", ") : "—"}</dd>
        <dt>{t("Horário")}</dt>
        <dd>{profile.preferred_time ? PREFERRED_TIME_LABEL[profile.preferred_time] : "—"}</dd>
        <dt>{t("Equipamentos")}</dt>
        <dd>{orDash(profile.equipment)}</dd>
        <dt>{t("Objetivos")}</dt>
        <dd style={{ whiteSpace: "pre-wrap" }}>{orDash(profile.goals_text)}</dd>
        <dt>{t("Histórico")}</dt>
        <dd style={{ whiteSpace: "pre-wrap" }}>{orDash(profile.training_history)}</dd>
        <dt>{t("FC máx. / repouso")}</dt>
        <dd className="num">
          {t("{max} / {rest} bpm", { max: profile.hr_max_bpm ?? "—", rest: profile.hr_rest_bpm ?? "—" })}
        </dd>
        <dt>{t("Faixas de intensidade (FC)")}</dt>
        <dd>
          {profile.hr_low_max_bpm && profile.hr_moderate_max_bpm ? (
            <span className="num">
              {t("Baixa até {hr_low_max_bpm} · Moderada até {hr_moderate_max_bpm} · Alta acima, em bpm", { hr_low_max_bpm: profile.hr_low_max_bpm, hr_moderate_max_bpm: profile.hr_moderate_max_bpm })}</span>
          ) : (
            <span className="muted">{profile.hr_max_bpm ? t("Não declaradas: usando % da FC máxima") : t("Não declaradas")}</span>
          )}
        </dd>
        {showCoachFields && (
          <>
            <dt>{t("Observações")}</dt>
            <dd style={{ whiteSpace: "pre-wrap" }}>{orDash(profile.sport_notes)}</dd>
          </>
        )}
      </dl>

      <div>
        <h3 style={{ marginBottom: 8 }}>{t("Melhores marcas")}</h3>
        {profile.best_marks.length === 0 ? (
          <p className="muted">{t("Nenhuma marca registrada.")}</p>
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>{t("Distância")}</th>
                  <th className="right">{t("Tempo")}</th>
                  <th className="right">{t("Ritmo")}</th>
                  <th>{t("Data")}</th>
                  <th>{t("Prova")}</th>
                </tr>
              </thead>
              <tbody>
                {[...profile.best_marks]
                  .sort((a, b) => a.distance_m - b.distance_m)
                  .map((m, i) => (
                    <tr key={i}>
                      <td>{distanceLabel(m.distance_m)}</td>
                      <td className="right num">{formatDuration(m.time_s)}</td>
                      <td className="right num">{formatPace(paceOf(m.time_s, m.distance_m))}</td>
                      <td>{formatDate(m.date)}</td>
                      <td>{orDash(m.race)}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showCoachFields && (
        <div>
          <h3 style={{ marginBottom: 8 }}>{t("Zonas de treinamento")}</h3>
          {profile.training_zones.length === 0 ? (
            <p className="muted">{t("Nenhuma zona configurada.")}</p>
          ) : (
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>{t("Zona")}</th>
                    <th className="right">{t("FC (bpm)")}</th>
                    <th className="right">{t("Ritmo (min/km)")}</th>
                  </tr>
                </thead>
                <tbody>
                  {profile.training_zones.map((z, i) => (
                    <tr key={i}>
                      <td>{z.name}</td>
                      <td className="right num">{rangeText(z.hr_min_bpm, z.hr_max_bpm)}</td>
                      <td className="right num">{rangeText(z.pace_min_s_km, z.pace_max_s_km, paceShort)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
